import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getApiLanguage } from "src/services/commonLanguageHeader";
import {
  cloneMappingPreset,
  createMappingPresetMapping,
  deleteMappingPreset,
  deleteMappingPresetMapping,
  exportMappingPreset,
  applyMappingPreset,
  getMappingPresetByDeviceType,
  getMappingPresetById,
  getMappingPresetNames,
  getMappingPresetProperties,
  getMappingPresets,
  updateMappingPresetMapping,
  type MappingPresetListParams,
  type MappingPresetPayload,
  type UpdateMappingPresetPayload,
} from "src/services/mappingPresetService";
import { useGeneralRefetchOverride } from "./general.queries";
import { QueryNames } from "./queries.constant";

type QueryOptions = { enabled?: boolean } & Record<string, unknown>;

export const useQueryGetMappingPresets = (
  params: MappingPresetListParams = {},
  options: QueryOptions = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const enabled = options.enabled !== false;

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.mappingPresetList, params, language],
      queryFn: () => getMappingPresets(params, language),
      enabled,
      ...options,
    }),
    enabled
  );
};

export const useQueryGetMappingPresetById = (
  presetId?: string | number,
  options: QueryOptions = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const enabled = !!presetId && options.enabled !== false;

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.mappingPresetDetail, presetId, language],
      queryFn: () => getMappingPresetById(presetId as string | number, language),
      enabled,
      ...options,
    }),
    enabled
  );
};

export const useQueryGetMappingPresetByDeviceType = (
  deviceTypeId?: string | number,
  options: QueryOptions = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const enabled = !!deviceTypeId && options.enabled !== false;

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.mappingPresetByDeviceType, deviceTypeId, language],
      queryFn: () => getMappingPresetByDeviceType(deviceTypeId, language),
      enabled,
      ...options,
    }),
    enabled
  );
};

export const useQueryGetMappingPresetNames = (enabled = true) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...QueryNames.mappingPresetNames, language],
    queryFn: () => getMappingPresetNames(language),
    enabled,
  });
};

export const useQueryGetMappingPresetProperties = (
  presetId?: string | number,
  options: QueryOptions = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const enabled = !!presetId && options.enabled !== false;

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.mappingPresetProperties, presetId, language],
      queryFn: () => getMappingPresetProperties(presetId as string | number, language),
      enabled,
      ...options,
    }),
    enabled
  );
};

export const useMutationCloneMappingPreset = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (params: { id: string | number; name: string }) =>
      cloneMappingPreset(params, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.mappingPresetList });
      queryClient.invalidateQueries({ queryKey: QueryNames.mappingPresetByDeviceType });
      queryClient.invalidateQueries({ queryKey: QueryNames.mappingPresetNames });
    },
  });
};

export const useMutationUseMappingPreset = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (params: { unitId: string | number; presetId: string | number }) =>
      applyMappingPreset(params, language),
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({
        queryKey: [...QueryNames.deviceMappings, params.unitId, language],
        exact: true,
      });
    },
  });
};

export const useMutationExportMappingPreset = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (params: { deviceMappingId: string | number; name: string }) =>
      exportMappingPreset(params, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.mappingPresetList });
      queryClient.invalidateQueries({ queryKey: QueryNames.mappingPresetByDeviceType });
      queryClient.invalidateQueries({ queryKey: QueryNames.mappingPresetNames });
    },
  });
};

export const useMutationCreateMappingPresetMapping = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: MappingPresetPayload) => createMappingPresetMapping(payload, language),
    onSuccess: (_data, payload) => {
      if (payload.presetId === undefined || payload.presetId === null) return;

      queryClient.invalidateQueries({
        queryKey: [...QueryNames.mappingPresetDetail, payload.presetId, language],
        exact: true,
      });
      queryClient.invalidateQueries({
        queryKey: [...QueryNames.mappingPresetProperties, payload.presetId, language],
        exact: true,
      });
    },
  });
};

export const useMutationUpdateMappingPresetMapping = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (params: {
      id: string | number;
      presetId: string | number;
      payload: UpdateMappingPresetPayload;
    }) => updateMappingPresetMapping(params, language),
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({
        queryKey: [...QueryNames.mappingPresetDetail, params.presetId, language],
        exact: true,
      });
      queryClient.invalidateQueries({
        queryKey: [...QueryNames.mappingPresetProperties, params.presetId, language],
        exact: true,
      });
    },
  });
};

export const useMutationDeleteMappingPresetMapping = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (params: { mappingId: string | number; presetId: string | number }) =>
      deleteMappingPresetMapping(params, language),
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({
        queryKey: [...QueryNames.mappingPresetDetail, params.presetId, language],
        exact: true,
      });
      queryClient.invalidateQueries({
        queryKey: [...QueryNames.mappingPresetProperties, params.presetId, language],
        exact: true,
      });
    },
  });
};

export const useMutationDeleteMappingPreset = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (presetId: string | number) => deleteMappingPreset(presetId, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.mappingPresetList });
      queryClient.invalidateQueries({ queryKey: QueryNames.mappingPresetByDeviceType });
      queryClient.invalidateQueries({ queryKey: QueryNames.mappingPresetNames });
    },
  });
};
